import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';

import { APP_ROUTES } from '../../app.constants';
import { CreateLeadRequest } from '../../core/interfaces/createLeadRequest';
import { CreateLeadComponent } from '../../core/modules/custom-controls/create-lead/create-lead.component';
import { OperationType } from '../../core/modules/custom-navigation';
import { UiOperationService } from '../../core/modules/custom-navigation/services/ui-operation.service';
import { UiOperationFactory } from '../../core/modules/custom-navigation/services/ui-opertaion.factory';
import { ROLES_STATUS } from '../../enums';
import { AccountDTO } from '../../interfaces/models';
import { LeadService } from '../../providers/leads/lead.service';
import { LoadingService } from '../../providers/loading/loading.service';
import { UserService } from '../../providers/user/user.service';
import { DirectivesModule } from '../../directives.module';
import { CustomViewsModule } from '../../core/modules/custom-views/custom-views.module';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    DirectivesModule,
    CustomViewsModule,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  private static readonly DEFAULT_AVATAR_URL = '/assets/image/avatar-default.png';
  nameRoute: string;
  notifications: number;
  notificationKey: string;
  roles = ROLES_STATUS;
  companyName: string;
  dotNumber: string;
  userSubscription: Subscription;
  @Input() user: AccountDTO;
  @Output() toggleMenu: EventEmitter<unknown> = new EventEmitter();

  constructor(
    private userService: UserService,
    private loadingService: LoadingService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private uiOperationService: UiOperationService,
    private uiOperationFactory: UiOperationFactory,
    private leadService: LeadService,
  ) { }

  ngOnInit() {
    this.setCompanyName();
    this.changeEventsRouter();
    this.setRouteName();
    this.userSubscription = this.userService
      .updateUser
      .subscribe((value) => {
        if (this.notificationKey) {
          this.notifications = value && value[this.notificationKey];
        }
      });
  }

  ngOnDestroy() {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  private setCompanyName() {
    const { companyName, company } = this.user;
    if (company) {
      this.companyName = company && company.name;
      this.dotNumber = company && company.dotNumber;
    }
    if (companyName && !company) {
      this.companyName = companyName;
    }
  }

  private changeEventsRouter() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.setRouteName();
      }
    });
  }

  private setRouteName() {
    let snapshot = this.route.snapshot;
    let activated = this.route.firstChild;
    if (activated != null) {
      while (activated != null) {
        snapshot = activated.snapshot;
        activated = activated.firstChild;
      }
    }
    this.nameRoute = snapshot.data['nameRoute'] || '';
    this.notificationKey = snapshot.data['notificationKey'] || '';

    if (this.notificationKey) {
      this.notifications = this.user && this.user[this.notificationKey];
    }
  }

  public toogleSideMenu() {
    this.toggleMenu.emit();
  }

  public async logout() {
    this.loadingService.startLoading();
    await this.userService.logout();
    this.loadingService.stopLoading();
  }

  public myProfile() {
    if (this.userService.user.roleId === ROLES_STATUS.SUPER_ADMIN) {
      this.router.navigate(['/profile']);
    } else {
      this.router.navigate(['/accounts', 'me']);
    }
  }

  public myCompany() {
    this.router.navigate([APP_ROUTES.my_company]);
  }

  public changePassword() {
    this.router.navigate([APP_ROUTES.reset_password]);
  }

  public createQuote() {
    this.dialog.open(CreateLeadComponent, { width: '700px', }).afterClosed().pipe(
      filter(x => !!x),
      switchMap((lead: CreateLeadRequest) => this.leadService.createLead(lead)),
      tap(() => {
        this.uiOperationService.next(this.uiOperationFactory.getOperation(OperationType.CreateLead))
      }),
    ).subscribe();
  }

  public getAvatarUrl(): string {
    const avatarUrl = (this.user && this.user.avatarUrl || '').trim();

    if (
      !avatarUrl
      || avatarUrl.includes('/replace_me/')
      || avatarUrl.startsWith('replace_me/')
    ) {
      return HeaderComponent.DEFAULT_AVATAR_URL;
    }

    return avatarUrl;
  }

  public onAvatarError(event: Event) {
    const element = event.target as HTMLImageElement | null;

    if (!element) {
      return;
    }

    element.src = HeaderComponent.DEFAULT_AVATAR_URL;
  }
}
